import posthog from 'posthog-js';
import type { CaptureResult } from 'posthog-js';
import { getPosthogApiHost, getPosthogApiKey } from '@/config/env-config';
import { getTenantSlugFromHostname } from '@/utils/path-utils';
import type { AnalyticsEventName } from './events';
import { scrubTelemetryUrl } from './scrub-url';

const TENANT_PLACEHOLDER = ':tenant';

// Tenant slugs are customer identifiers — they live ONLY in the group key.
// web-login is served on the tenant subdomain: 'acme.x.com' → ':tenant.x.com'
export function anonymizeTenantInHost(value: string, slug: string): string {
  if (!slug) {
    return value;
  }

  return value.replace(
    new RegExp(`(^|//)${slug}\\.`, 'g'),
    `$1${TENANT_PLACEHOLDER}.`
  );
}

// Scrub every string value, not a key list — the SDK keeps growing URL-bearing
// properties ($pathname, $prev_pageview_pathname, $session_entry_*, ...)
export function scrubEventUrls(event: CaptureResult | null) {
  if (!event) {
    return null;
  }

  const slug = getTenantSlugFromHostname();

  for (const bag of [event.properties, event.$set, event.$set_once]) {
    if (!bag) {
      continue;
    }

    for (const [key, value] of Object.entries(bag)) {
      if (typeof value === 'string') {
        bag[key] = anonymizeTenantInHost(scrubTelemetryUrl(value), slug);
      }
    }
  }

  // Stable breakdown key; equals the scrubbed $pathname
  if (typeof event.properties?.$pathname === 'string') {
    event.properties.route = event.properties.$pathname;
  }

  return event;
}

let initialized = false;

export function initializePosthog(): void {
  const apiKey = getPosthogApiKey();

  if (!apiKey) {
    return;
  }

  try {
    posthog.init(apiKey, {
      api_host: getPosthogApiHost(),
      // Toolbar and app links resolve against the PostHog UI, not the ingestion host
      ui_host: 'https://us.posthog.com',
      defaults: '2026-06-25',
      // Manual-events-only model (SPRK-6015): interactions are tracked solely via
      // captureAnalyticsEvent — no DOM serialization ever leaves the browser
      autocapture: false,
      person_profiles: 'identified_only',
      // Replay stays off client-side even if someone enables it on the project (SPRK-6008)
      disable_session_recording: true,
      // The toolbar was the only lazy-loaded dependency and CSP blocks its
      // CSS + posthog.com auth — it mounts as unstyled icons, then bails
      disable_external_dependency_loading: true,
      disable_surveys: true,
      // /flags posts raw $initial_* URLs (tenant host) and skips before_send
      advanced_disable_flags: true,
      before_send: scrubEventUrls,
    });
    posthog.register({ app: 'web-login' });

    const slug = getTenantSlugFromHostname();

    if (slug) {
      posthog.group('tenant', slug);
    }

    initialized = true;
  } catch (error) {
    console.error('[PostHog] Failed to initialize:', error);
  }
}

export type AnalyticsProperties = Record<
  string,
  string | number | boolean | undefined
>;

// The ONLY interaction-tracking mechanism. Properties must be enums/counts —
// never user content (names, emails, values).
export function captureAnalyticsEvent(
  name: AnalyticsEventName,
  properties?: AnalyticsProperties
): void {
  if (!initialized) {
    return;
  }

  try {
    posthog.capture(name, properties);
  } catch (error) {
    console.error('[PostHog] Failed to capture event:', error);
  }
}
